/** Sending feedback: what kind, what happened, and an optional way to reply. */

import { useState } from 'react';
import type { Locale } from '@garbage/shared';
import { useT } from '../i18n.js';

export type FeedbackKind = 'bug' | 'idea' | 'other';
type Status = 'editing' | 'sending' | 'sent' | 'failed' | 'tooMany';

export type FeedbackPayload = {
  kind: FeedbackKind; message: string; contact?: string; locale: Locale; screen: string;
};

/** Post it to the server. Kept apart from the form so tests can swap it out. */
export async function sendFeedback(payload: FeedbackPayload, post: typeof fetch = fetch): Promise<'sent' | 'failed' | 'tooMany'> {
  try {
    const response = await post('/api/feedback', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (response.status === 429) return 'tooMany';
    return response.ok ? 'sent' : 'failed';
  } catch {
    return 'failed';
  }
}

const KINDS: FeedbackKind[] = ['bug', 'idea', 'other'];

export function FeedbackForm({
  screen, locale, onDone, onBack, send = sendFeedback,
}: {
  screen: string;
  locale: Locale;
  onDone: () => void;
  onBack: () => void;
  send?: (payload: FeedbackPayload) => Promise<'sent' | 'failed' | 'tooMany'>;
}) {
  const t = useT();
  const [kind, setKind] = useState<FeedbackKind>('idea');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [status, setStatus] = useState<Status>('editing');

  async function submit() {
    if (!message.trim() || status === 'sending') return;
    setStatus('sending');
    const outcome = await send({
      kind, message: message.trim(), locale, screen,
      ...(contact.trim() ? { contact: contact.trim() } : {}),
    });
    setStatus(outcome);
  }

  if (status === 'sent') {
    return (
      <div className="text-center" data-testid="feedback-sent">
        <p className="mb-2 text-4xl" aria-hidden="true">💌</p>
        <p className="mb-4 font-bold">{t('feedback.thanks')}</p>
        <button type="button" onClick={onDone} className="w-full rounded-2xl bg-sun py-3 font-black text-ink">
          {t('common.done')}
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="feedback-form"
      onSubmit={(event) => { event.preventDefault(); void submit(); }}
      className="flex flex-col gap-3"
    >
      <h2 className="text-center text-xl font-black text-sun">{t('feedback.title')}</h2>
      <p className="-mt-1 text-center text-sm text-bone/60">{t('feedback.intro')}</p>

      <div className="grid grid-cols-3 gap-2">
        {KINDS.map((option) => (
          <button
            key={option}
            type="button"
            data-testid={`feedback-kind-${option}`}
            aria-pressed={kind === option}
            onClick={() => setKind(option)}
            className={[
              'rounded-xl px-1 py-2 text-xs font-bold leading-tight',
              kind === option ? 'bg-sun text-ink' : 'bg-white/10',
            ].join(' ')}
          >
            {t(`feedback.kind.${option}`)}
          </button>
        ))}
      </div>

      <label className="block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-bone/50">{t('feedback.message')}</span>
        <textarea
          data-testid="feedback-message"
          value={message}
          maxLength={2000}
          rows={5}
          onChange={(event) => setMessage(event.target.value)}
          className="w-full rounded-xl bg-white/10 px-3 py-2 text-base text-bone"
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-bone/50">{t('feedback.contact')}</span>
        <input
          data-testid="feedback-contact"
          type="email"
          value={contact}
          maxLength={120}
          autoComplete="email"
          onChange={(event) => setContact(event.target.value)}
          className="w-full rounded-xl bg-white/10 px-3 py-2 text-base text-bone"
        />
      </label>
      <p className="-mt-2 text-[11px] text-bone/40">{t('feedback.private')}</p>

      {(status === 'failed' || status === 'tooMany') && (
        <p role="alert" data-testid="feedback-error" className="text-center text-sm font-bold text-coral">
          {status === 'tooMany' ? t('feedback.tooMany') : t('feedback.failed')}
        </p>
      )}

      <div className="flex gap-2">
        <button type="button" onClick={onBack} className="min-h-12 flex-1 rounded-2xl bg-white/10 font-bold">
          {t('common.back')}
        </button>
        <button
          type="submit"
          data-testid="feedback-send"
          disabled={!message.trim() || status === 'sending'}
          className="min-h-12 flex-[2] rounded-2xl bg-sun font-black text-ink disabled:opacity-30"
        >
          {status === 'sending' ? t('feedback.sending') : t('feedback.send')}
        </button>
      </div>
    </form>
  );
}
